'use client'

import type {
  SelectOption,
  SelectProps,
} from '@pearlconvert/ui/components/select/select.types'

import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useClickOutsideElement } from '@pearlconvert/ui/hooks/use-click-outside-element'
import { useBreakpoint } from '@pearlconvert/ui/providers/breakpoint/breakpoint.context'
import { useDisabledPageScroll } from '@pearlconvert/ui/providers/disabled-page-scroll/disabled-page-scroll.context'
import { useModals } from '@pearlconvert/ui/providers/modals/modals.context'

export const useSelect = ({
  field,
  handleChange,
  initialSelectedOption,
  label,
  placeholder,
  name,
  options,
}: SelectProps) => {
  const getSelectedIndex = useCallback(
    (value?: string) => options.findIndex((option) => option.value === value),
    [options]
  )

  const initialSelected =
    options?.find((option) => option.value === field?.value) ||
    initialSelectedOption

  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false)
  const [activeIndex, setActiveIndex] = useState<number>(
    getSelectedIndex(initialSelected?.value)
  )
  const [selectedOption, setSelectedOption] = useState<
    SelectOption | undefined
  >(initialSelected)
  const [isFocus, setIsFocus] = useState<boolean>(false)
  const [modalInitialized, setModalInitialized] = useState<boolean>(false)

  const listRef = useRef<HTMLUListElement>(null) as RefObject<HTMLUListElement>
  const searchRef = useRef<string>('')
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const selectButtonRef = useRef<HTMLButtonElement | null>(null)
  const optionRefs = useRef<(HTMLLIElement | null)[]>([])

  const { toggleDisabledPageScroll } = useDisabledPageScroll()
  const { isMobileView, isDesktopView } = useBreakpoint()
  const isMobile = isMobileView()

  const { addModal, openModal, closeModal, updateModal } = useModals()

  const clear = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
    }
  }

  const onClose = useCallback(() => {
    setIsDropdownOpen(false)
    if (isMobile) {
      closeModal(name)
      // Return focus to select button after modal closes
      setTimeout(() => {
        selectButtonRef.current?.focus()
      }, 100)
    }

    setActiveIndex(getSelectedIndex(selectedOption?.value))
    optionRefs.current?.[activeIndex]?.blur()
  }, [
    activeIndex,
    closeModal,
    isMobile,
    name,
    selectedOption,
    getSelectedIndex,
  ])

  useClickOutsideElement(wrapperRef, () => {
    if (isDesktopView()) {
      onClose()
    }
  })

  useEffect(() => () => clear(), [])

  useEffect(() => {
    setSelectedOption(initialSelected)
  }, [initialSelected])

  useEffect(() => {
    setActiveIndex(getSelectedIndex(selectedOption?.value))
  }, [selectedOption, getSelectedIndex])

  const resetTypeahead = useCallback(() => {
    searchRef.current = ''
    clear()
  }, [])

  const handleToggleDropdown = useCallback(() => {
    if (isMobile) {
      openModal(name)
      setIsDropdownOpen(true)
    } else {
      setIsDropdownOpen((prevState) => !prevState)
    }
  }, [isMobile, name, openModal])

  const toggleDropdown = useCallback(
    (state: boolean) => {
      if (!state) {
        resetTypeahead()
      }

      setActiveIndex(state ? getSelectedIndex(selectedOption?.value) : -1)
      setIsDropdownOpen(state)

      if (isMobile) {
        toggleDisabledPageScroll(state)
        closeModal(name)
      }
    },
    [
      closeModal,
      getSelectedIndex,
      isMobile,
      name,
      resetTypeahead,
      selectedOption?.value,
      toggleDisabledPageScroll,
    ]
  )

  const select = useCallback(
    (value: string) => {
      const selectedOption = options.find(
        (option) => option.value === value
      ) as SelectOption

      if (value) {
        setSelectedOption(selectedOption)
        handleChange?.(selectedOption)
        field?.onChange(selectedOption?.value)
      }
      toggleDropdown(false)
      resetTypeahead()

      // Return focus to select button after selection for accessibility
      setTimeout(() => {
        selectButtonRef.current?.focus()
      }, 0)
    },
    [field, handleChange, options, toggleDropdown, resetTypeahead]
  )

  const wrapArray = <T>(array: T[], startIndex: number): T[] => {
    if (startIndex >= array.length) {
      startIndex = 0
    }
    return [...array.slice(startIndex), ...array.slice(0, startIndex)]
  }

  const findNextItem = useCallback(
    (
      items: SelectOption[],
      search: string,
      currentItem?: SelectOption
    ): SelectOption | undefined => {
      const isRepeated =
        search.length > 1 &&
        Array.from(search).every((char) => char === search[0])
      const normalizedSearch = isRepeated ? search[0] : search

      const currentItemIndex = currentItem
        ? items.findIndex((item) => item.value === currentItem.value)
        : -1

      const wrappedItems = wrapArray(items, currentItemIndex + 1)

      const nextItem = wrappedItems.find((item) =>
        (typeof item.label === 'string' ? item.label : item.textLabel || '')
          .toLowerCase()
          .startsWith(normalizedSearch.toLowerCase())
      )

      return nextItem !== currentItem ? nextItem : undefined
    },
    []
  )

  const handleTypeaheadSearch = useCallback(
    (key: string) => {
      searchRef.current += key

      clear()
      timerRef.current = setTimeout(() => {
        searchRef.current = ''
      }, 1000)

      const nextItem = findNextItem(options, searchRef.current, selectedOption)

      if (nextItem) {
        setSelectedOption(nextItem)
        setActiveIndex(getSelectedIndex(nextItem.value))
      }
    },
    [options, selectedOption, findNextItem, getSelectedIndex]
  )

  const openDropdownKeyDownCallbackHandlers = useCallback(
    (event: KeyboardEvent) => {
      event.preventDefault()

      if (/^[a-z0-9]$/i.test(event.key)) {
        handleTypeaheadSearch(event.key)
        return
      }

      let newIndex = activeIndex

      switch (event.key) {
        case 'ArrowUp':
        case 'Up':
          newIndex = activeIndex > 0 ? activeIndex - 1 : options.length - 1
          break

        case 'ArrowDown':
        case 'Down':
          newIndex = activeIndex < options.length - 1 ? activeIndex + 1 : 0
          break

        case 'Enter':
        case ' ':
          select(options[activeIndex]?.value)
          onClose()
          return

        case 'Escape':
          optionRefs.current?.[activeIndex]?.blur()
          onClose()
          return

        case 'Tab':
          onClose()
          return
      }

      setActiveIndex(newIndex)
      optionRefs.current[newIndex]?.focus()
    },
    [activeIndex, handleTypeaheadSearch, onClose, options, select]
  )

  const closedDropdownKeyDownCallbackHandlers = useCallback(
    (event: KeyboardEvent) => {
      switch (event.key) {
        case 'Up':
        case 'ArrowUp':
        case 'Down':
        case 'ArrowDown':
        case ' ':
        case 'Enter':
          event.preventDefault()
          toggleDropdown(true)
      }
    },
    [toggleDropdown]
  )

  useEffect(() => {
    if (isDropdownOpen) {
      document.addEventListener('keydown', openDropdownKeyDownCallbackHandlers)
      document.removeEventListener(
        'keydown',
        closedDropdownKeyDownCallbackHandlers
      )
    } else {
      if (isFocus) {
        document.addEventListener(
          'keydown',
          closedDropdownKeyDownCallbackHandlers
        )
      }
      document.removeEventListener(
        'keydown',
        openDropdownKeyDownCallbackHandlers
      )
    }

    return () => {
      document.removeEventListener(
        'keydown',
        openDropdownKeyDownCallbackHandlers
      )
      document.removeEventListener(
        'keydown',
        closedDropdownKeyDownCallbackHandlers
      )
    }
  }, [
    isDropdownOpen,
    isFocus,
    closedDropdownKeyDownCallbackHandlers,
    openDropdownKeyDownCallbackHandlers,
  ])

  useEffect(() => {
    if (!isDropdownOpen || !listRef.current) {
      return
    }

    const indexToScroll =
      activeIndex >= 0
        ? activeIndex
        : optionRefs.current.findIndex(
            (el) => el?.getAttribute('aria-selected') === 'true'
          )

    if (indexToScroll >= 0) {
      const el = optionRefs.current[indexToScroll]

      el?.scrollIntoView?.({ block: 'nearest' })
    }
  }, [isDropdownOpen, activeIndex])

  const getLabelDisplay = useMemo(() => {
    if (!selectedOption?.value) {
      return placeholder || label
    }

    // In the case of complex labels,
    // this solution may not display all elements and is not recommended for use

    // if (React.isValidElement(selectedOption.label)) {
    //   const children = selectedOption.label.props.children
    //   return Array.isArray(children) ? children[0] : children
    // }

    return selectedOption.textLabel || selectedOption.label
  }, [label, placeholder, selectedOption])

  const handleMouseClickAction = useCallback(
    (value: string) => {
      select(value)
      onClose?.()
    },
    [select, onClose]
  )

  const handleMouseLeaveOptionAction = useCallback(() => {
    if (!isMobile) {
      setActiveIndex(-1)
    }
  }, [isMobile])

  return {
    activeIndex,
    addModal,
    getLabelDisplay,
    handleMouseLeaveOptionAction,
    handleMouseClickAction,
    handleToggleDropdown,
    isDesktop: isDesktopView(),
    isDropdownOpen,
    isMobile: isMobileView(),
    listRef,
    modalInitialized,
    setModalInitialized,
    onClose,
    openModal,
    optionRefs,
    selectAction: select,
    selectButtonRef,
    selectedOption,
    setActiveIndexAction: setActiveIndex,
    setIsDropdownOpen,
    setIsFocus,
    toggleDropdown,
    updateModal,
    wrapperRef,
  }
}
